"use client";
/**
 * Baked default layout — the app's built-in arrangement.
 *
 * `defaultLayout.json` is a snapshot of the owner's fully-arranged localStorage
 * (workspace trees, split-divider positions, page grid positions, per-pane
 * symbols, chart config, scanner + nav preferences), captured from the browser
 * and pruned of the disposable bar cache (`mpp:chart:bars:*`) and empty drawing
 * keys. It seeds a fresh / empty profile and powers "Reset to my layout", so the
 * arrangement is the default everywhere and survives a full storage wipe.
 *
 * Row heights are part of an arrangement. A vertical split is a stack of pixel
 * rows (`VerticalFillStack`, components/workspace/resizableTree.tsx): every row
 * but the last reads `mpp:ws:rowh:{nsKey}:{stackId}:v1`, a row with nothing
 * saved starts at 380px, and the last row takes what is left. The snapshot was
 * taken when a vertical split was still a percentage group, so its trees came
 * with `react-resizable-panels:` sizes that nothing reads for a stack any more
 * and no `mpp:ws:rowh:*`. That is harmless while a tree's stacks fill the page
 * panel. It was not once a stack sat INSIDE a row: its first pane took the
 * whole row and the rest were pushed out of the box, with nothing to scroll.
 * The Daily Brief became that tree when the Market Bets migration wrapped its
 * three columns in a new root stack, and a new profile opened it with five of
 * nine panes cut off (found 2026-10-03). Its entry was brought up to date by
 * hand on 2026-10-04: Market Bets is in the tree (so the migration adds
 * nothing and the root keeps its id), each of its three stacks has its row
 * heights, and its page panel is 52 rows. `npm run wsreflow:check` holds the
 * entry to that.
 *
 * The same day the tree stopped cutting panes off: rows that do not fit their
 * box share it or the stack scrolls, and a row nobody dragged that holds
 * columns of panes starts as tall as those columns need
 * (lib/workspace/rowStack.ts, README principle 65). Without its heights the
 * Daily Brief would now open with every pane reachable and none squeezed: the
 * row of columns 1,544px tall, each pane of the middle column 380px. That is
 * a readable page, but not the sizes it was arranged at (Breadth Dashboard
 * 1,040px, Market Performance 496px). The heights are still what opens it as
 * arranged, so a snapshot taken again must come from a profile whose rows
 * were dragged, or carry these keys over.
 *
 * Server-canonical override: on load, PrefsSyncGate hydrates localStorage from
 * the user's server prefs FIRST, then calls {@link seedDefaultLayoutIfFresh}. We
 * only seed when the profile is genuinely empty (no synced key survived
 * hydration), so an existing user's own layout is never clobbered.
 */
import { safeLocalStorageSet, isSyncedKey } from "@/lib/safeLocalStorage";

/** Set once the seed has been applied (or intentionally skipped) so we never
 *  re-seed over a profile the user has since edited. */
const APPLIED_MARKER = "mpp:default-layout:applied:v1";

/** The ~500 KB snapshot is lazy-loaded (dynamic import) so it never rides in the
 *  shared client bundle — only a genuinely fresh profile or an explicit reset
 *  ever fetches it. */
async function loadLayout(): Promise<Record<string, string>> {
  const mod = await import("./defaultLayout.json");
  return (mod.default ?? mod) as Record<string, string>;
}

/** The keys this snapshot manages — every synced pref plus the (local-only)
 *  react-resizable-panels divider positions and the disposable bar cache. Used
 *  by the reset path to wipe a profile clean before re-applying, so the result
 *  matches the snapshot exactly. */
function managedKeys(): string[] {
  const out: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k || k === APPLIED_MARKER) continue;
    if (isSyncedKey(k) || k.startsWith("react-resizable-panels:") || k.startsWith("mpp:chart:bars:")) out.push(k);
  }
  return out;
}

/** True when the profile already holds a user arrangement — any synced `mpp:*`
 *  key beyond our own marker means the server hydrated something (or the user
 *  built a layout), so we must NOT seed over it. */
function hasExistingLayout(): boolean {
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k !== APPLIED_MARKER && isSyncedKey(k)) return true;
  }
  return false;
}

/** Write every key of the baked snapshot into localStorage. Synced (`mpp:*`)
 *  keys go through safeLocalStorageSet so they mirror to the server; the
 *  local-only divider keys are written raw. */
function writeSnapshot(layout: Record<string, string>): void {
  for (const [k, v] of Object.entries(layout)) {
    if (k.startsWith("mpp:")) {
      safeLocalStorageSet(k, v);
    } else {
      try {
        localStorage.setItem(k, v);
      } catch {
        /* quota — non-fatal, the chart self-heals on next write */
      }
    }
  }
}

/**
 * Seed the baked default IF the profile is fresh. Called once, after prefs
 * hydration, from PrefsSyncGate. Returns true if the seed was applied.
 * Idempotent via the applied-marker.
 */
export async function seedDefaultLayoutIfFresh(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  try {
    if (localStorage.getItem(APPLIED_MARKER)) return false;
    if (hasExistingLayout()) {
      // The user already has an arrangement (their own, server-hydrated). Mark
      // so we never seed later, but leave their layout untouched. (No snapshot
      // fetch on this common path.)
      try {
        localStorage.setItem(APPLIED_MARKER, "1");
      } catch {
        /* ignore */
      }
      return false;
    }
    writeSnapshot(await loadLayout());
    try {
      localStorage.setItem(APPLIED_MARKER, "1");
    } catch {
      /* ignore */
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Force-apply the baked default over the current profile — the "Reset to my
 * layout" action. Wipes every managed key first so the result exactly matches
 * the snapshot, then re-applies. The caller should reload the page afterwards so
 * every workspace / chart re-reads the restored state.
 */
export async function resetToDefaultLayout(): Promise<void> {
  if (typeof window === "undefined") return;
  const layout = await loadLayout();
  for (const k of managedKeys()) {
    try {
      localStorage.removeItem(k);
    } catch {
      /* ignore */
    }
  }
  writeSnapshot(layout);
  try {
    localStorage.setItem(APPLIED_MARKER, "1");
  } catch {
    /* ignore */
  }
}
